// ============================================================
// WAITLIST PAGE — Unified data source for all user-facing copy,
// field labels, placeholders, select option arrays, and inline
// validation messages used by WaitlistPage.jsx.
// The page should import from this file only (plus CS_SPECIALIZATIONS
// from home-data.js).
// ============================================================

export const WAITLIST_PAGE = {
    // ── Page header ──────────────────────────────────────────
    header: {
        back: "Back",
        titleLine1: "Join the Waitlist.",
        titleLine2: "Be part of what's next.",
        description:
            "Founding members get early access, exclusive pricing, and priority placement.",
    },

    // ── Success screen ───────────────────────────────────────
    success: {
        title: "You're In!",
        message:
            "Welcome to the AI Prof founding cohort. We'll be in touch within 24 hours with your next steps and exclusive founding-member access.",
        backToHome: "Back to Home",
    },

    // ── Submit / toast copy ──────────────────────────────────
    submit: {
        idle: "Claim My Spot →",
        loading: "Submitting...",
        successToast: "You're on the list! 🎉",
        errorToast: "Something went wrong. Please try again.",
        alreadyRegisteredToast: "You have already registered with AI.Prof. Our team will reach out to you within the next 24 hours.",
    },

    // ── Role selector ────────────────────────────────────────
    roleLabel: "Select Category",
    roleOptions: [
        { value: "student", label: "Student" },
        { value: "university", label: "University / Institution" },
        { value: "company", label: "Company / Recruiter" },
    ],

    // ── Checkbox group helper text ───────────────────────────
    checkboxHint: "(Select multiple)",

    // ── Default placeholder for select fields ────────────────
    selectPlaceholder: "Select...",

    // ── STUDENT FORM ─────────────────────────────────────────
    student: {
        sections: {
            personalInfo: "Personal Info",
            academicInfo: "Academic Info",
            areaOfInterest: "Area of Interest",
            discovery: "Discovery",
        },
        fields: {
            fullName: { label: "Full Name", placeholder: "Rahul Sharma" },
            email: { label: "Email Address", placeholder: "rahul@example.com" },
            mobileNumber: { label: "Mobile Number", placeholder: "9876543210" },
            cityLocation: { label: "City / Location", placeholder: "Mumbai, Maharashtra" },
            collegeName: { label: "College / University Name", placeholder: "IIT Delhi" },
            branchDepartment: { label: "Branch / Department", placeholder: "Computer Science" },
            yearOfStudy: { label: "Year of Study" },
            expectedGraduationYear: { label: "Expected Graduation Year", placeholder: "2025" },
            tracksOfInterest: { label: "Tracks of Interest" },
            discoverySource: { label: "How did you hear about AI.Prof?" },
            discoveryDetails: { label: "Additional Details (Optional)", placeholder: "Tell us more..." },
        },
        options: {
            yearOfStudy: ["1st Year", "2nd Year", "3rd Year", "4th Year", "Graduated"],
            discoverySource: ["College friend", "Social media", "Faculty", "Event", "Other"],
        },
    },

    // ── UNIVERSITY FORM ──────────────────────────────────────
    university: {
        sections: {
            institutionInfo: "Institution Info",
            pointOfContact: "Point of Contact",
            existingSetup: "Existing Setup",
            partnershipIntent: "Partnership Intent",
        },
        fields: {
            institutionName: { label: "Institution Name", placeholder: "ABC Engineering College" },
            institutionType: { label: "Institution Type" },
            institutionTypeOther: { placeholder: "Please specify institution type..." },
            affiliatedUniversity: { label: "Affiliated University (if applicable)", placeholder: "Anna University" },
            yearOfEstablishment: { label: "Year of Establishment", placeholder: "1995" },
            city: { label: "City", placeholder: "Chennai" },
            state: { label: "State", placeholder: "Tamil Nadu" },
            websiteUrl: { label: "Website URL", placeholder: "https://college.edu.in" },
            techStudentsCount: { label: "Total CS Students", placeholder: "3000" },
            contactPersonName: { label: "Contact Person's Full Name", placeholder: "Dr. Priya Singh" },
            designationRole: { label: "Designation / Role", placeholder: "e.g. Principal, HOD, Training & Placement Officer" },
            officialEmail: { label: "Official Email Address", placeholder: "placements@college.edu.in" },
            mobileNumber: { label: "Mobile Number", placeholder: "9876543210" },
            department: { label: "Department (if applicable)", placeholder: "Computer Science" },
            hasPlacementCell: { label: "Dedicated Training & Placement Cell?" },
            partneredWithEdtech: { label: "Partnered with any EdTech platforms?" },
            edtechPartners: { label: "EdTech Platforms (if yes)", placeholder: "Platform names" },
            conductsCampusDrives: { label: "Campus Recruitment Drives?" },
            currentPlacementRate: { label: "Current Average Placement Rate (%)", placeholder: "75" },
            lookingFor: { label: "What are you looking for?" },
            deploymentTimeline: { label: "Preferred Deployment Timeline" },
            additionalNotes: { label: "Additional Notes or Requirements", placeholder: "Any specific requirements or questions..." },
        },
        options: {
            institutionType: ["Engineering College", "Deemed University", "Autonomous College", "University", "Other"],
            yesNo: ["Yes", "No"],
            lookingFor: ["Student Upskilling", "Placement Track", "Four Year CS Programme", "Not Sure Yet"],
            deploymentTimeline: ["Immediately", "Next Semester", "Next Academic Year", "Just Exploring"],
        },
    },

    // ── COMPANY / RECRUITER FORM ─────────────────────────────
    company: {
        sections: {
            companyInfo: "Company Info",
            registrationLocation: "Company Registration & Location",
            companyDetails: "Company Details",
            primaryContact: "Primary Contact Person",
            hiringRequirements: "Hiring Requirements",
            hiringTimelineDiscovery: "Hiring Timeline & Discovery",
        },
        fields: {
            companyName: { label: "Company Legal Name", placeholder: "Tech Solutions Pvt Ltd" },
            industry: { label: "Industry", placeholder: "Information Technology" },
            officialWebsite: { label: "Official Website", placeholder: "https://company.com" },
            companyRegistration: { label: "Company Registration Details (CIN/LLC/Pvt Ltd/Ltd)", placeholder: "U72900DL2020PTC123456" },
            address: { label: "Address", placeholder: "Registered office address" },
            state: { label: "State", placeholder: "Karnataka" },
            city: { label: "City", placeholder: "Bangalore" },
            employeeStrength: { label: "Employee Strength" },
            companyOverview: { label: "Company Overview", placeholder: "Brief description of your company for student visibility (optional)..." },
            linkedinUrl: { label: "LinkedIn Profile URL", placeholder: "https://linkedin.com/company/..." },
            socialMediaHandles: { label: "Other Social Media Handles", placeholder: "e.g. Twitter, Instagram...", addAnother: "Add another" },
            contactName: { label: "Contact Name", placeholder: "Amit Kumar" },
            designation: { label: "Designation", placeholder: "HR Manager" },
            workEmail: { label: "Work Email", placeholder: "amit@company.com" },
            phoneNumber: { label: "Phone Number", placeholder: "9876543210" },
            hiringVolume: { label: "Hiring Volume (Quarterly/Annual)", placeholder: "20-50 per year" },
            candidateExperience: { label: "Ideal Candidate Experience" },
            preferredSkills: { label: "Preferred Skills & Domains" },
            recruitmentStartDate: { label: "Expected Recruitment Start Date" },
            discoverySource: { label: "How did you hear about us?" },
        },
        options: {
            employeeStrength: ["1-10", "10-50", "50-200", "200-500", "500-1000", "1000+"],
            candidateExperience: ["Freshers", "1-3 Years", "3+ Years", "Open to All"],
            preferredSkills: ["Full Stack", "AI/ML", "Data Science", "Cloud", "DevOps", "Cybersecurity", "Mobile Dev", "SDE"],
            discoverySource: ["LinkedIn", "Google Search", "Referral", "Event", "Social Media", "Other"],
        },
    },

    // ── Inline validation messages ───────────────────────────
    validation: {
        fullNameRequired: "Full name is required",
        nameInvalid: "Enter a valid name",
        emailRequired: "Email is required",
        emailInvalid: "Enter a valid email address",
        mobileRequired: "Mobile number is required",
        mobileTenDigits: "Mobile number should be 10 digits",
        cityLocationRequired: "City / location is required",
        collegeNameRequired: "College name is required",
        branchDepartmentRequired: "Branch / department is required",
        yearOfStudyRequired: "Please select your year of study",
        graduationYearRequired: "Graduation year is required",
        graduationYearInvalid: "Enter a valid year (e.g. 2025)",
        trackRequired: "Select at least one track",
        discoverySourceRequired: "Please select an option",

        institutionNameRequired: "Institution name is required",
        institutionTypeRequired: "Please select institution type",
        institutionTypeOtherRequired: "Please specify the institution type",
        establishmentYearRequired: "Year of establishment is required",
        establishmentYearInvalid: "Enter a valid year (e.g. 1995)",
        establishmentFuture: "Enter the correct year of establishment",
        cityRequired: "City is required",
        stateRequired: "State is required",
        fieldRequired: "This field is required",
        numberInvalid: "Enter a valid number",
        contactNameRequired: "Contact name is required",
        designationRequired: "Designation is required",
        placementCellRequired: "Please select an option",
        placementRateRequired: "Placement rate is required",
        percentInvalid: "Enter a percentage between 0 and 100",
        lookingForRequired: "Select at least one option",
        timelineRequired: "Please select a timeline",
        urlInvalid: "Enter a valid URL (https://...)",

        companyNameRequired: "Company name is required",
        industryRequired: "Industry is required",
        registrationRequired: "Registration details are required",
        employeeStrengthRequired: "Please select employee strength",
        workEmailRequired: "Work email is required",
        phoneRequired: "Phone number is required",
        phoneTenDigits: "Phone number should be 10 digits",
        hiringVolumeRequired: "Hiring volume is required",
        candidateExperienceRequired: "Please select candidate experience",
        skillRequired: "Select at least one skill",
    },
};
